/**
 * 管理后台：系统设置（标题栏 / 首页 / 底图提供商等）
 */
import { useEffect, useState } from 'react';
import { message } from '../utils/antdMessage';
import { DEFAULT_HOMEPAGE_SETTINGS, normalizeHomepageSettings } from '../utils/homepageSettings';
import {
  DEFAULT_MAP_PROVIDER_SETTINGS,
  normalizeMapProviderSettings
} from '../utils/mapProviderSettings';

export default function useAdminSystemSettings({ apiRequest }) {
  const [systemSettings, setSystemSettings] = useState({
    titleBar: {
      text: '龙泉市户外应急救援队 - 现场指挥中心',
      fontSize: 24,
      letterSpacing: 0,
      backgroundColor: '#2c3e50',
      textColor: '#ffffff',
      height: 60,
      fontWeight: 'bold',
      show: true
    },
    navigationBar: {
      height: 32,
      backgroundColor: '#4176a7',
      textColor: '#ffffff',
      fontSize: 16,
      linkSpacing: 32,
      linkPadding: 16,
      show: true
    },
    largeScreen: {
      titleBarHeight: 80,
      titleBarFontSize: 32,
      navigationBarHeight: 36,
      breakpoint: 1920
    },
    mobileTitleBar: {
      text: '龙泉市户外应急救援队 - 现场指挥中心',
      height: 50,
      fontSize: 18,
      backgroundColor: '#2c3e50',
      textColor: '#ffffff',
      letterSpacing: 0,
      fontWeight: 'bold',
      show: true
    },
    frontlineSettings: {
      defaultOnlineHours: 24,
      showRescueTargets: true,
      showTracks: true,
      showMembers: true,
      onlineHoursOptions: [1, 6, 12, 24, 48, 168],
      enableSmartFilter: false
    },
    pickPointSettings: {
      recordAccuracyMaxM: 15,
      tooLargeAccuracyM: 50
    },
    rescuePushSettings: {
      defaultIcon: 'flag',
      defaultLevel: '普通',
      pickPointTtlMinutes: 30,
      trackerTtlDays: 30
    },
    homepageSettings: JSON.parse(JSON.stringify(DEFAULT_HOMEPAGE_SETTINGS)),
    mapProviderSettings: normalizeMapProviderSettings(DEFAULT_MAP_PROVIDER_SETTINGS)
  });
  const [systemSettingsMsg, setSystemSettingsMsg] = useState('');
  const [homepageSettingsTab, setHomepageSettingsTab] = useState('pc');

  const fetchSystemSettings = async () => {
    try {
      console.log('AdminPanel: 开始获取系统设置...');
      
      // 检查是否有token
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('AdminPanel: 没有token，跳过系统设置获取');
        return;
      }
      
      const data = await apiRequest('/api/admin/system-settings');
      console.log('AdminPanel: 获取到的原始数据:', data);
      
      if (data.success && data.settings) {
        console.log('AdminPanel: 使用后端设置:', data.settings);
        // 与本地默认结构合并，避免缺字段导致设置页渲染崩溃
        const normalized = {
          ...systemSettings,
          ...data.settings,
          titleBar: { ...(systemSettings.titleBar || {}), ...(data.settings.titleBar || {}) },
          navigationBar: { ...(systemSettings.navigationBar || {}), ...(data.settings.navigationBar || {}) },
          mobileTitleBar: { ...(systemSettings.mobileTitleBar || {}), ...(data.settings.mobileTitleBar || {}) },
          mobileNavigationBar: { ...(systemSettings.mobileNavigationBar || {}), ...(data.settings.mobileNavigationBar || {}) },
          largeScreen: { ...(systemSettings.largeScreen || {}), ...(data.settings.largeScreen || {}) },
          frontlineSettings: { ...(systemSettings.frontlineSettings || {}), ...(data.settings.frontlineSettings || {}) },
          pickPointSettings: {
            recordAccuracyMaxM: 15,
            tooLargeAccuracyM: 50,
            ...(systemSettings.pickPointSettings || {}),
            ...(data.settings.pickPointSettings || {})
          },
          rescuePushSettings: {
            defaultIcon: 'flag',
            defaultLevel: '普通',
            pickPointTtlMinutes: 30,
            trackerTtlDays: 30,
            ...(systemSettings.rescuePushSettings || {}),
            ...(data.settings.rescuePushSettings || {})
          },
          homepageSettings: normalizeHomepageSettings(data.settings.homepageSettings || systemSettings.homepageSettings),
          mapProviderSettings: normalizeMapProviderSettings(data.settings.mapProviderSettings || systemSettings.mapProviderSettings)
        };
        setSystemSettings(normalized);
        // 立即应用设置到CSS变量
        applySystemSettings(normalized);
      } else {
        console.log('AdminPanel: 后端数据无效，使用默认设置');
        console.log('AdminPanel: data.success =', data.success);
        console.log('AdminPanel: data.settings =', data.settings);
        console.log('AdminPanel: Object.keys(data.settings).length =', data.settings ? Object.keys(data.settings).length : 'undefined');
        // 后端返回空对象或无效数据，使用默认设置
        const defaultSettings = {
          titleBar: {
            text: '龙泉市户外应急救援队 - 现场指挥中心',
            fontSize: 24,
            letterSpacing: 0,
            backgroundColor: '#2c3e50',
            textColor: '#ffffff',
            height: 60,
            fontWeight: 'bold',
            show: true
          },
          navigationBar: {
            height: 32,
            backgroundColor: '#4176a7',
            textColor: '#ffffff',
            fontSize: 16,
            linkSpacing: 32,
            linkPadding: 16,
            show: true
          },
          largeScreen: {
            titleBarHeight: 80,
            titleBarFontSize: 32,
            navigationBarHeight: 36,
            breakpoint: 1920
          },
          mobileTitleBar: {
            text: '龙泉市户外应急救援队 - 现场指挥中心',
            height: 50,
            fontSize: 18,
            backgroundColor: '#2c3e50',
            textColor: '#ffffff',
            letterSpacing: 0,
            fontWeight: 'bold',
            show: true
          },
          homepageSettings: JSON.parse(JSON.stringify(DEFAULT_HOMEPAGE_SETTINGS)),
          mapProviderSettings: normalizeMapProviderSettings(DEFAULT_MAP_PROVIDER_SETTINGS)
        };
        setSystemSettings(defaultSettings);
        applySystemSettings(defaultSettings);
      }
    } catch (error) {
      console.error('获取系统设置失败:', error);
      // 出错时也使用默认设置
      const defaultSettings = {
        titleBar: {
          text: '龙泉市户外应急救援队 - 现场指挥中心',
          fontSize: 24,
          letterSpacing: 0,
          backgroundColor: '#2c3e50',
          textColor: '#ffffff',
          height: 60,
          fontWeight: 'bold',
          show: true
        },
        navigationBar: {
          height: 32,
          backgroundColor: '#4176a7',
          textColor: '#ffffff',
          fontSize: 16,
          linkSpacing: 32,
          linkPadding: 16,
          show: true
        },
        largeScreen: {
          titleBarHeight: 80,
          titleBarFontSize: 32,
          navigationBarHeight: 36,
          breakpoint: 1920
        },
        mobileTitleBar: {
          text: '龙泉市户外应急救援队 - 现场指挥中心',
          height: 50,
          fontSize: 18,
          backgroundColor: '#2c3e50',
          textColor: '#ffffff',
          letterSpacing: 0,
          fontWeight: 'bold',
          show: true
        },
        homepageSettings: JSON.parse(JSON.stringify(DEFAULT_HOMEPAGE_SETTINGS)),
        mapProviderSettings: normalizeMapProviderSettings(DEFAULT_MAP_PROVIDER_SETTINGS)
      };
      setSystemSettings(defaultSettings);
      applySystemSettings(defaultSettings);
    }
  };

  // 保存系统设置
  const handleSaveSystemSettings = async () => {
    try {
      const payload = {
        ...systemSettings,
        homepageSettings: normalizeHomepageSettings(systemSettings.homepageSettings),
        mapProviderSettings: normalizeMapProviderSettings(systemSettings.mapProviderSettings)
      };
      console.log('=== 开始保存系统设置 ===');
      console.log('要保存的设置:', JSON.stringify(payload, null, 2));
      
      const response = await apiRequest('/api/admin/system-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      console.log('保存响应:', response);

      if (response.success) {
        message.success('系统设置保存成功');
        
        // 更新本地缓存，供访客/登录页同步读取
        try {
          localStorage.setItem('backendSystemSettings', JSON.stringify(payload));
          localStorage.setItem('systemSettingsLastFetch', Date.now().toString());
          localStorage.setItem('systemSettings', JSON.stringify(payload));
          localStorage.setItem('ui_settings', JSON.stringify(payload));
        } catch (e) {
          console.warn('缓存系统设置失败:', e);
        }

        setSystemSettings(payload);

        // 直接应用新设置到CSS变量
        applySystemSettings(payload);
        
        console.log('设置已应用到CSS变量');
        
        // 触发自定义事件，通知App.js重新应用设置
        window.dispatchEvent(new CustomEvent('systemSettingsUpdated', {
          detail: { settings: payload }
        }));
        
        console.log('系统设置已保存并应用:', payload);
      } else {
        console.error('保存失败:', response.error);
        message.error('保存失败: ' + response.error);
      }
    } catch (error) {
      console.error('保存系统设置失败:', error);
      message.error('保存失败，请检查网络连接');
    }
  };

  // 实时预览系统设置（不通知App.js）
  const previewSystemSettings = (settings) => {
    console.log('=== 实时预览系统设置 ===');
    console.log('预览设置:', JSON.stringify(settings, null, 2));
    
    // 安全检查：确保settings存在且包含必要的属性
    if (!settings || !settings.titleBar || !settings.navigationBar) {
      console.warn('系统设置不完整，使用默认设置');
      const defaultSettings = {
        titleBar: {
          text: '龙泉市户外应急救援队 - 现场指挥中心',
          fontSize: 24,
          letterSpacing: 0,
          backgroundColor: '#2c3e50',
          textColor: '#ffffff',
          height: 60,
          fontWeight: 'bold',
          show: true
        },
        navigationBar: {
          height: 32,
          backgroundColor: '#4176a7',
          textColor: '#ffffff',
          fontSize: 16,
          linkSpacing: 32,
          linkPadding: 16,
          show: true
        }
      };
      settings = defaultSettings;
    }
    
    // 立即应用CSS变量实现实时预览
    const root = document.documentElement;
    
    // 标题栏设置（添加!important确保优先级）
    root.style.setProperty('--title-bar-height', `${settings.titleBar.height}px`, 'important');
    root.style.setProperty('--title-bar-bg-color', settings.titleBar.backgroundColor, 'important');
    root.style.setProperty('--title-bar-text-color', settings.titleBar.textColor, 'important');
    root.style.setProperty('--title-bar-font-size', `${settings.titleBar.fontSize}px`, 'important');
    root.style.setProperty('--title-bar-font-weight', settings.titleBar.fontWeight, 'important');
    root.style.setProperty('--title-bar-letter-spacing', `${settings.titleBar.letterSpacing}px`, 'important');
    
    // 导航栏设置（添加!important确保优先级）
    root.style.setProperty('--nav-bar-height', `${settings.navigationBar.height}px`, 'important');
    root.style.setProperty('--nav-bar-bg-color', settings.navigationBar.backgroundColor, 'important');
    root.style.setProperty('--nav-bar-text-color', settings.navigationBar.textColor, 'important');
    root.style.setProperty('--nav-bar-font-size', `${settings.navigationBar.fontSize}px`, 'important');
    root.style.setProperty('--nav-bar-link-spacing', `${settings.navigationBar.linkSpacing ?? 0}px`, 'important');
    root.style.setProperty('--nav-bar-link-padding-x', `${settings.navigationBar.linkPadding ?? 16}px`, 'important');
    
    // 手机端标题栏设置
    if (settings.mobileTitleBar) {
      root.style.setProperty('--mobile-title-bar-height', `${settings.mobileTitleBar.height}px`, 'important');
      root.style.setProperty('--mobile-title-bar-bg-color', settings.mobileTitleBar.backgroundColor, 'important');
      root.style.setProperty('--mobile-title-bar-text-color', settings.mobileTitleBar.textColor, 'important');
      root.style.setProperty('--mobile-title-bar-font-size', `${settings.mobileTitleBar.fontSize}px`, 'important');
      root.style.setProperty('--mobile-title-bar-font-weight', settings.mobileTitleBar.fontWeight, 'important');
      root.style.setProperty('--mobile-title-bar-letter-spacing', `${settings.mobileTitleBar.letterSpacing}px`, 'important');
    }
    
    // 手机端导航栏设置（预览时若在手机布局也会用到通用 --nav-bar-*）
    if (settings.mobileNavigationBar) {
      const secondRowHeight = settings.mobileNavigationBar.secondRowHeight || 36;
      const firstRowHeight = settings.mobileNavigationBar.firstRowHeight
        ?? Math.max(24, (settings.mobileNavigationBar.height || 76) - secondRowHeight);
      const mobileNavHeight = firstRowHeight + secondRowHeight;
      root.style.setProperty('--mobile-nav-bar-height', `${mobileNavHeight}px`, 'important');
      root.style.setProperty('--mobile-first-row-height', `${firstRowHeight}px`, 'important');
      root.style.setProperty('--mobile-second-row-height', `${secondRowHeight}px`, 'important');
      root.style.setProperty('--mobile-nav-bar-bg-color', settings.mobileNavigationBar.backgroundColor, 'important');
      root.style.setProperty('--mobile-nav-bar-text-color', settings.mobileNavigationBar.textColor, 'important');
      root.style.setProperty('--mobile-nav-bar-font-size', `${settings.mobileNavigationBar.fontSize}px`, 'important');
      root.style.setProperty('--mobile-nav-bar-link-spacing', `${settings.mobileNavigationBar.linkSpacing ?? 0}px`, 'important');
      root.style.setProperty('--mobile-nav-bar-link-padding-x', `${settings.mobileNavigationBar.linkPadding ?? 8}px`, 'important');
    }
    
    // 计算总头部高度
    const titleHeight = settings.titleBar.show ? settings.titleBar.height : 0;
    const navHeight = settings.navigationBar.height;
    const totalHeight = titleHeight + navHeight;
    root.style.setProperty('--header-total-height', `${totalHeight}px`, 'important');
    
    // 实时更新标题栏文本
    const titleBarElement = document.querySelector('.header-title-text');
    if (titleBarElement) {
      titleBarElement.textContent = settings.titleBar.text;
      console.log('标题栏文本已更新为:', settings.titleBar.text);
    } else {
      console.warn('未找到标题栏文本元素 .header-title-text');
    }
    
    // 验证CSS变量是否正确设置
    const computedFontSize = getComputedStyle(root).getPropertyValue('--title-bar-font-size');
    console.log('CSS变量设置验证:');
    console.log('- 设置的字体大小:', settings.titleBar.fontSize + 'px');
    console.log('- 实际CSS变量值:', computedFontSize);
    console.log('- 标题栏元素:', titleBarElement);
    
    // 强制重新计算样式 - 查找所有可能的标题栏元素
    const titleBarElements = document.querySelectorAll('.header-title-text, .header-title-bar .header-title-text, [class*="title"]');
    console.log('找到的标题栏元素数量:', titleBarElements.length);
    
    titleBarElements.forEach((element, index) => {
      console.log(`更新标题栏元素 ${index}:`, element);
      element.style.fontSize = `${settings.titleBar.fontSize}px`;
      element.style.fontWeight = settings.titleBar.fontWeight;
      element.style.letterSpacing = `${settings.titleBar.letterSpacing}px`;
      element.style.color = settings.titleBar.textColor;
      if (element.textContent !== settings.titleBar.text) {
        element.textContent = settings.titleBar.text;
      }
    });
    
    // 同时更新标题栏容器的背景色和高度
    const titleBarContainers = document.querySelectorAll('.header-title-bar');
    titleBarContainers.forEach((container, index) => {
      console.log(`更新标题栏容器 ${index}:`, container);
      container.style.height = `${settings.titleBar.height}px`;
      container.style.backgroundColor = settings.titleBar.backgroundColor;
    });
    
    console.log('直接应用内联样式作为备用方案，已处理', titleBarElements.length, '个标题栏元素');
    
    console.log('CSS变量和标题栏文本已更新，实时预览生效');
    
    // 不发送事件给App.js，避免重新获取设置覆盖当前预览
    // 只在保存时才通知App.js
    console.log('实时预览模式：不通知App.js，避免设置被覆盖');
  };

  // 应用系统设置到CSS变量（保存时使用，会通知App.js）
  const applySystemSettings = (settings) => {
    // 先应用预览效果
    previewSystemSettings(settings);
    
    // 然后通知App.js更新
    console.log('保存模式：通知App.js应用设置');
    window.dispatchEvent(new CustomEvent('systemSettingsUpdated', {
      detail: { settings: settings }
    }));
  };

  // 更新系统设置并立即应用预览
  const updateSystemSettings = (updater) => {
    setSystemSettings(prev => {
      const newSettings = updater(prev);
      // 立即应用新设置进行实时预览（不通知App.js）
      previewSystemSettings(newSettings);
      return newSettings;
    });
  };

  // 页面加载时应用当前设置
  useEffect(() => {
    if (systemSettings && systemSettings.titleBar) {
      console.log('AdminPanel页面加载，应用当前系统设置进行预览');
      previewSystemSettings(systemSettings);
    }
  }, [systemSettings]);

  return {
    systemSettings, systemSettingsMsg, homepageSettingsTab, setHomepageSettingsTab,
    fetchSystemSettings, handleSaveSystemSettings, updateSystemSettings, applySystemSettings
  };
}
